import { ref, computed, watch } from 'vue';
import { v4 as uuidv4 } from "uuid";
import { EquipmentType, EquipmentConfiguration } from '@/types/v2';
import { useEquipment } from "@/composables/tasks/useEquipment";
import { useTaskHistory } from '@/composables/tasks/useTaskHistory';

let instance: ReturnType<typeof createEquipmentForm> | null = null;

export function createEquipmentForm() {
  // ===================================================================
  // Types & interfaces
  // ===================================================================
  interface EquipmentFormState {
    editMode: boolean;
    addMode: boolean;
    selectedEquipmentTypeId: string | number | null;
    values: Record<string, any>;
    originalConfig: EquipmentConfiguration | null;
    errors: Record<string, string[]>;
    isSubmitting: boolean;
  }

  // ===================================================================
  // Composables
  // ===================================================================
  const {
    getEquipmentTypes,
    updateEquipmentConfiguration,
    createEquipmentInConfiguration,
    formatFieldName,
    getOptionLabel
  } = useEquipment();

  const taskHistory = useTaskHistory();

  // ===================================================================
  // State
  // ===================================================================
  const formState = ref<EquipmentFormState>({
    editMode: false,
    addMode: false,
    selectedEquipmentTypeId: null,
    values: {},
    originalConfig: null,
    errors: {},
    isSubmitting: false
  });

  // ===================================================================
  // Computed
  // ===================================================================
  const selectedEquipmentType = computed<EquipmentType | null>(() => {
    if (!formState.value.selectedEquipmentTypeId) return null;

    return getEquipmentTypes.value.find(
        (type: { id: string | number | null; }) => type.id === formState.value.selectedEquipmentTypeId
    ) || null;
  });

  // ===================================================================
  // Methods
  // ===================================================================
  // Reset the form state
  const resetForm = () => {
    formState.value = {
      editMode: false,
      addMode: false,
      selectedEquipmentTypeId: null,
      values: {},
      originalConfig: null,
      errors: {},
      isSubmitting: false
    };
  };

  // Clear validation errors
  const clearErrors = () => {
    formState.value.errors = {};
  };

  // Initialize form for creating a new configuration
  const initAddForm = (equipmentTypeId: string | number) => {
    resetForm();
    formState.value.addMode = true;
    formState.value.selectedEquipmentTypeId = equipmentTypeId;

    // Initialize with empty values
    initializeValues();
  };

  // Initialize form for editing an existing configuration
  const initEditForm = (config: EquipmentConfiguration) => {
    resetForm();
    formState.value.editMode = true;
    formState.value.selectedEquipmentTypeId = config.equipmentType.id;
    formState.value.originalConfig = config;

    // Clone values to avoid direct mutation
    formState.value.values = JSON.parse(JSON.stringify(config.values || {}));
  };

  // Initialize default values based on equipment type
  const initializeValues = () => {
    console.log(selectedEquipmentType.value);
    if (!selectedEquipmentType.value) return;

    const values: Record<string, any> = {};
    selectedEquipmentType.value.configOptions.forEach(option => {
      if (option.type === 'boolean') {
        values[option.name] = false;
      } else if (option.type === 'number') {
        values[option.name] = null;
      } else if (option.type === 'select' && option.options && option.options.length > 0) {
        values[option.name] = option.options[0];
      } else {
        values[option.name] = '';
      }
    });

    formState.value.values = values;
  };

  // Basic validation
  const validateForm = (): boolean => {
    clearErrors();
    const errors: Record<string, string[]> = {};

    if (!selectedEquipmentType.value) {
      errors['general'] = ['No equipment type selected'];
      formState.value.errors = errors;
      return false;
    }

    // Check required fields
    selectedEquipmentType.value.configOptions.forEach(option => {
      if (option.required) {
        const value = formState.value.values[option.name];
        if (value === null || value === undefined || value === '') {
          const fieldName = option.label || formatFieldName(option.name);
          if (!errors[option.name]) {
            errors[option.name] = [];
          }
          errors[option.name].push(`${fieldName} is required`);
        }
      }
    });

    formState.value.errors = errors;
    return Object.keys(errors).length === 0;
  };

  // Submit the form - handles both add and edit
  const handleSubmit = async () => {
    if (!validateForm()) return;

    formState.value.isSubmitting = true;

    try {
      if (formState.value.editMode) {
        await saveConfigChanges();
      } else if (formState.value.addMode) {
        await saveNewConfig();
      }
      resetForm();
    } catch (error) {
      console.error('Error saving equipment configuration:', error);
      formState.value.errors['general'] = ['Failed to save configuration'];
    } finally {
      formState.value.isSubmitting = false;
    }
  };

  // Save changes to an existing configuration
  const saveConfigChanges = async (): Promise<void> => {
    if (!formState.value.originalConfig) return;

    const originalConfig = formState.value.originalConfig;
    let hasChanges = false;

    // Check each value for changes
    for (const key of Object.keys(formState.value.values)) {
      const oldValue = originalConfig.values[key];
      const newValue = formState.value.values[key];

      // Only record changes if values are different
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        hasChanges = true;

        // Get proper field label
        const optionDef = originalConfig.equipmentType.configOptions.find(opt => opt.name === key);
        const fieldLabel = optionDef
          ? getOptionLabel(originalConfig.equipmentType.configOptions, key)
          : formatFieldName(key);

        // Record the change
        await taskHistory.recordChange({
          fieldPath: `equipment.${originalConfig.id}.values.${key}`,
          fieldName: `${originalConfig.equipmentType.name}: ${fieldLabel}`,
          oldValue,
          newValue,
          context: `Equipment: ${originalConfig.equipmentType.name}`,
          contextType: 'equipment',
          workflowStage: 'configuration',
          requireJustification: optionDef?.require_justification_for_change !== false
        });
      }
    }

    // Only update if there were actual changes
    if (hasChanges) {
      const updatedConfig = {
        ...originalConfig,
        values: { ...formState.value.values },
        updated_at: new Date().toISOString()
      };

      updateEquipmentConfiguration(updatedConfig);
    }
  };

  // Save a new configuration
  const saveNewConfig = async (): Promise<void> => {
    if (!selectedEquipmentType.value) return;

    // Create the new configuration
    const newConfig: EquipmentConfiguration = {
      id: uuidv4(),
      equipmentType: selectedEquipmentType.value,
      values: { ...formState.value.values },
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // Record the addition
    await taskHistory.recordChange({
      fieldPath: `equipment.add`,
      fieldName: 'Add Equipment Configuration',
      oldValue: null,
      newValue: selectedEquipmentType.value.name,
      context: `Equipment: ${selectedEquipmentType.value.name}`,
      contextType: 'equipment',
      workflowStage: 'configuration',
      requireJustification: true,
    });

    // Add to task
    createEquipmentInConfiguration(newConfig);
  };

  // Cancel form editing/adding
  const cancelForm = () => {
    resetForm();
  };

  // When equipment type changes, initialize default values
  watch(() => formState.value.selectedEquipmentTypeId, (newVal) => {
    if (newVal && formState.value.addMode) {
      initializeValues();
    }
  });

  return {
    // ===================================================================
    // State
    // ===================================================================
    formState,
    selectedEquipmentType,

    // ===================================================================
    // Methods
    // ===================================================================
    initAddForm,
    initEditForm,
    handleSubmit,
    cancelForm,
    clearErrors
  };
}

export function useEquipmentForm() {
  if (!instance) {
    instance = createEquipmentForm();
  }
  return instance;
}